










import { defineComponent, useMeta, useRoute, ref, onMounted, useContext } from '@nuxtjs/composition-api'
import Navbar from '~/components/feature/datasheets/Navbar.vue'
import Datasheet from '~/components/feature/datasheets/main/Datasheet.vue'
import ProtocolDatasheet from '~/components/feature/datasheets/protocol/ProtocolDatasheet.vue'
import ImageDatasheet from '~/components/feature/datasheets/image/ImageDatasheet.vue'
import { useProductStore } from '~/store/product'
import { usePdpStore } from '~/store/pdp'
import { useCloudflareStore } from '~/store'
import { PRODUCT_TYPES } from '@/common/utils/product/constants'
import { pageContentSize, datasheetPrintMargins, pixelsPerInch } from '~/common/constants/datasheet'
import { decodeHTMLEntities } from '~/common/utils/characters'
import { useCanonicalUrl } from '~/composables/useBaseUrl'
import LogMiddleware from '@/middleware/log'
import { fetchManyGlobalSettings } from '@/middleware/with-data'

export default defineComponent({
  components: {
    Navbar,
    Datasheet,
    ImageDatasheet,
    ProtocolDatasheet,
  },
  layout: 'empty',
  middleware: [LogMiddleware],
  setup() {
    const route = useRoute()
    const { currentProduct } = useProductStore()
    const mainDivRef = ref<any>()
    const ctx = useContext()

    const product = currentProduct.data
    const productType = currentProduct.data.productType
    const showImages = route.value.query.images === '1'
    const size: 'a4' | 'letter' = (route.value.query.size as string)?.toLowerCase() === 'a4' ? 'a4' : 'letter'
    const datasheetWidth = (pageContentSize[size].width * pixelsPerInch) / 16 // convert from inches to rem

    const showProtocol =
      route.value.query.protocol === '1' &&
      (productType === PRODUCT_TYPES.FAST_SCAN_ELISA_KIT ||
        productType === PRODUCT_TYPES.ELISA_KIT ||
        productType === PRODUCT_TYPES.ELISA_ANTIBODY_PAIR ||
        productType === PRODUCT_TYPES.OLIGO_ANTIBODY_PAIR)

    useMeta({
      title: `${decodeHTMLEntities(product.name)} (#${product.key}) Datasheet ${
        showImages ? 'With' : 'Without'
      } Images`,
      link: [
        {
          rel: 'canonical',
          media: 'all',
          href: useCanonicalUrl(product.attributes.sefUrl),
        },
        {
          rel: 'alternate',
          hreflang: 'en',
          href: ctx.$config.cst.domainDefault + route.value.fullPath,
        },
        {
          rel: 'alternate',
          hreflang: 'zh-CN',
          href: ctx.$config.cst.domainChina + route.value.fullPath,
        },
        { rel: 'alternate', hreflang: 'ja-JP', href: ctx.$config.cst.domainJapan + route.value.fullPath },
        { rel: 'alternate', hreflang: 'x-default', href: ctx.$config.cst.domainDefault + route.value.fullPath },
      ],
    })

    onMounted(() => {
      const styleElement = document.createElement('style')
      // NOTE: despite the fact that page dimensions are calculated precisely,
      //  when printing, some browsers move the footer to the next page (new page is not added,
      //  only its content is moved). To fix this, set page margins to 90% of the correct value
      //  to account for this behavior.
      styleElement.innerHTML = `
        @page {
          size: ${size};
          margin-top: ${datasheetPrintMargins.height}in;
          margin-bottom: ${datasheetPrintMargins.height}in;
          margin-left: ${datasheetPrintMargins.width}in;
          margin-right: ${datasheetPrintMargins.width}in;
          padding: 0;
        }

        * {
          widows: 1 !important;
          break-inside: auto !important;
        }
        `
      mainDivRef.value.append(styleElement)
    })

    return {
      product,
      showImages,
      size,
      showProtocol,
      datasheetWidth,
      mainDivRef,
    }
  },
  async asyncData(ctx) {
    const productId = ctx.route.params.id
    const withImages = ctx.route.query?.images
    const productStore = useProductStore(ctx.$pinia)
    const pdpStore = usePdpStore(ctx.$pinia)
    const cloudflareStore = useCloudflareStore(ctx.$pinia)

    const promises: Promise<any>[] = [
      productStore.fetchFullProduct(productId),
      fetchManyGlobalSettings(), // layout data is required to show brand logo
    ]

    if (withImages) {
      promises.push(cloudflareStore.fetchCloudflareProductImages(productId))
    }

    await Promise.all(promises)

    if (!pdpStore.pdpPages[productId]) {
      await pdpStore.fetchPDPPage(productId)
    }
  },
  head: {}, // left empty intentionally, required for useMeta to work properly
})
